import { Button, Dimmer, Header, Loader, Segment } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { MODES_REGLEMENTS, MODES_REGLEMENTS_POINTAGE } from '@/constants';
import { ModeReglementContext } from '@/contexts';
import { useBordereauRemise, useEcheances } from '@/hooks';
import { deleteBordereau } from '@/routes';
import type { ModeReglementsPointageType } from '@/types';
import { isBordereauTitrageAFaire } from '@/utils';
import { useRegieStore } from '@/zustand';
import BordereauListe from './BordereauListe';
import BordereauModeSelector from './BordereauModeSelector';

const BordereauRemise = () => {
  const [modeActif, setModeActif] = React.useState<ModeReglementsPointageType | null>(null);

  const [deleting, setDeleting] = React.useState(false);

  const { regieModesReglement } = React.useContext(ModeReglementContext);
  const dateFinDernierArretePeriodique = useRegieStore(
    (state) => state.regie?.dateFinDernierArrete,
  );
  const { echeanceArrete } = useEcheances();

  // Filtrer les modes de pointage selon les modes autorisés de la régie
  const modesReglementsPointageFiltered = React.useMemo(
    () =>
      _.pickBy(MODES_REGLEMENTS_POINTAGE, (mode, code) =>
        _.some(regieModesReglement, (regieMode) => regieMode.code === code),
      ),
    [regieModesReglement],
  );

  const contextCheque = useBordereauRemise(MODES_REGLEMENTS.CHEQUE);
  const contextNumeraire = useBordereauRemise(MODES_REGLEMENTS.NUMERAIRE);
  const contextCESU = useBordereauRemise(MODES_REGLEMENTS.CESU);
  const contextChequeVac = useBordereauRemise(MODES_REGLEMENTS.CHEQUE_VAC);
  const contextCarteBancaire = useBordereauRemise(MODES_REGLEMENTS.CARTE_BANCAIRE);
  const contextVirement = useBordereauRemise(MODES_REGLEMENTS.VIREMENT);
  const contextPrelevement = useBordereauRemise(MODES_REGLEMENTS.PRELEVEMENT);
  const contextCarteBancaireInternet = useBordereauRemise(MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET);
  const contextPrelevementInternet = useBordereauRemise(MODES_REGLEMENTS.PRELEVEMENT_INTERNET);
  const contextTicket = useBordereauRemise(MODES_REGLEMENTS.TICKET);
  const contextBonCCAS = useBordereauRemise(MODES_REGLEMENTS.BON_CCAS);
  const contextBonCAF = useBordereauRemise(MODES_REGLEMENTS.BON_CAF);

  const currentContext = React.useMemo(() => {
    switch (modeActif) {
      case MODES_REGLEMENTS.CHEQUE:
        return contextCheque;
      case MODES_REGLEMENTS.NUMERAIRE:
        return contextNumeraire;
      case MODES_REGLEMENTS.CESU:
        return contextCESU;
      case MODES_REGLEMENTS.CHEQUE_VAC:
        return contextChequeVac;
      case MODES_REGLEMENTS.CARTE_BANCAIRE:
        return contextCarteBancaire;
      case MODES_REGLEMENTS.VIREMENT:
        return contextVirement;
      case MODES_REGLEMENTS.PRELEVEMENT:
        return contextPrelevement;
      case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
        return contextCarteBancaireInternet;
      case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
        return contextPrelevementInternet;
      case MODES_REGLEMENTS.TICKET:
        return contextTicket;
      case MODES_REGLEMENTS.BON_CCAS:
        return contextBonCCAS;
      case MODES_REGLEMENTS.BON_CAF:
        return contextBonCAF;
      default:
        return null;
    }
  }, [
    modeActif,
    contextCheque,
    contextNumeraire,
    contextCESU,
    contextChequeVac,
    contextCarteBancaire,
    contextVirement,
    contextPrelevement,
    contextCarteBancaireInternet,
    contextPrelevementInternet,
    contextTicket,
    contextBonCCAS,
    contextBonCAF,
  ]);

  const {
    bordereaux: currentBordereaux,
    setBordereaux: setCurrentBordereaux,
    total: currentTotal,
    setTotal: setCurrentTotal,
  } = currentContext || {};

  const hasOneLoading = React.useMemo(
    () =>
      contextCheque.loading ||
      contextNumeraire.loading ||
      contextCESU.loading ||
      contextChequeVac.loading ||
      contextCarteBancaire.loading ||
      contextVirement.loading ||
      contextPrelevement.loading ||
      contextCarteBancaireInternet.loading ||
      contextPrelevementInternet.loading ||
      contextTicket.loading ||
      contextBonCCAS.loading ||
      contextBonCAF.loading,
    [
      contextCheque.loading,
      contextNumeraire.loading,
      contextCESU.loading,
      contextChequeVac.loading,
      contextCarteBancaire.loading,
      contextVirement.loading,
      contextPrelevement.loading,
      contextCarteBancaireInternet.loading,
      contextPrelevementInternet.loading,
      contextTicket.loading,
      contextBonCCAS.loading,
      contextBonCAF.loading,
    ],
  );

  const handleModeChange = React.useCallback((e, { value }) => {
    setModeActif(value);
  }, []);

  const getTotalByCode = React.useCallback(
    (code: ModeReglementsPointageType) => {
      switch (code) {
        case MODES_REGLEMENTS.CHEQUE:
          return contextCheque.total;
        case MODES_REGLEMENTS.NUMERAIRE:
          return contextNumeraire.total;
        case MODES_REGLEMENTS.CESU:
          return contextCESU.total;
        case MODES_REGLEMENTS.CHEQUE_VAC:
          return contextChequeVac.total;
        case MODES_REGLEMENTS.CARTE_BANCAIRE:
          return contextCarteBancaire.total;
        case MODES_REGLEMENTS.VIREMENT:
          return contextVirement.total;
        case MODES_REGLEMENTS.PRELEVEMENT:
          return contextPrelevement.total;
        case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
          return contextCarteBancaireInternet.total;
        case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
          return contextPrelevementInternet.total;
        case MODES_REGLEMENTS.TICKET:
          return contextTicket.total;
        case MODES_REGLEMENTS.BON_CCAS:
          return contextBonCCAS.total;
        case MODES_REGLEMENTS.BON_CAF:
          return contextBonCAF.total;
        default:
          return 0;
      }
    },
    [
      contextCheque.total,
      contextNumeraire.total,
      contextCESU.total,
      contextChequeVac.total,
      contextCarteBancaire.total,
      contextVirement.total,
      contextPrelevement.total,
      contextCarteBancaireInternet.total,
      contextPrelevementInternet.total,
      contextTicket.total,
      contextBonCCAS.total,
      contextBonCAF.total,
    ],
  );

  const handleTransfertComptable = React.useCallback(() => {
    // TODO: Implémenter l'action de transfert comptable
    console.log('Accéder au transfert comptable');
  }, []);

  // Bordereau le plus récent : la liste est triée par date décroissante,
  // donc le premier élément correspond au dernier bordereau généré.
  // C'est le seul que l'on peut supprimer depuis cet écran.
  const dernierBordereau = currentBordereaux?.[0];

  // Verrou métier : on interdit la suppression si le dernier bordereau a été généré
  // avant la date de fin du dernier arrêté périodique de la régie, car il appartient
  // à une période déjà clôturée comptablement.
  const dernierBordereauAvantArrete = React.useMemo(() => {
    if (!dernierBordereau || !dateFinDernierArretePeriodique) return false;
    return dayjs(dernierBordereau.dateRemise).isBefore(
      dayjs(dateFinDernierArretePeriodique),
      'day',
    );
  }, [dernierBordereau, dateFinDernierArretePeriodique]);

  const aBordereauATitrer = React.useMemo(
    () =>
      _.some(currentBordereaux, (bordereau) =>
        isBordereauTitrageAFaire(bordereau, echeanceArrete?.date),
      ),
    [currentBordereaux, echeanceArrete?.date],
  );

  const handleSupprimerDernierBordereau = React.useCallback(() => {
    if (_.isEmpty(currentBordereaux) || !dernierBordereau) return;

    setDeleting(true);

    request
      .delete(deleteBordereau(dernierBordereau.identifiant))
      .then(() => {
        toast('Le bordereau a bien été supprimé', { type: 'success' });
        // Supprimer l'élément du tableau en front
        setCurrentBordereaux?.(
          currentBordereaux.filter((b) => b.identifiant !== dernierBordereau.identifiant),
        );
        // Mettre à jour le total
        setCurrentTotal?.(currentTotal - 1);
      })
      .catch(() => {
        toast('Une erreur est survenue lors de la suppression du bordereau', { type: 'error' });
      })
      .finally(() => {
        setDeleting(false);
      });
  }, [currentBordereaux, dernierBordereau, setCurrentBordereaux, currentTotal, setCurrentTotal]);

  // Initialiser le mode actif quand les modes autorisés sont chargés
  React.useEffect(() => {
    if (_.isNil(modeActif)) {
      const premierMode = _.head(
        _.keys(modesReglementsPointageFiltered),
      ) as ModeReglementsPointageType;

      setModeActif(premierMode);
    }
  }, [modeActif, modesReglementsPointageFiltered]);

  if (_.isNil(modeActif)) {
    return (
      <Dimmer active inverted>
        <Loader inverted>Chargement des modes de règlement de la régie</Loader>
      </Dimmer>
    );
  }

  return (
    <Segment
      inverted
      secondary
      style={{
        flex: 1,
        overflow: 'auto',
        background: 'transparent',
      }}
    >
      <Segment
        style={{
          borderRadius: 14,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <Header as="h1" style={{ alignSelf: 'center', marginBottom: '1em' }}>
          <Header.Content>Bordereaux de remise</Header.Content>
        </Header>
        <BordereauModeSelector
          modeActif={modeActif}
          loading={hasOneLoading}
          disabled={hasOneLoading}
          getTotalByCode={getTotalByCode}
          onModeChange={handleModeChange}
        />
        <BordereauListe type={modeActif} />
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: '1em',
            width: '100%',
            marginTop: '1em',
          }}
        >
          <Button
            positive
            loading={deleting}
            disabled={_.isEmpty(currentBordereaux) || deleting || dernierBordereauAvantArrete}
            onClick={handleSupprimerDernierBordereau}
          >
            {`Supprimer le bordereau n°${dernierBordereau?.numero ?? ''}`}
          </Button>
          {aBordereauATitrer && (
            <Button positive disabled={deleting} onClick={handleTransfertComptable}>
              Accéder au transfert comptable
            </Button>
          )}
        </div>
      </Segment>
    </Segment>
  );
};

export default BordereauRemise;
